import type {
  FeedFilters,
  RebookingCandidate,
  RebookingEventKind,
  RebookingCandidateStatus,
  RiskKind,
  UnconfirmedRow,
  PatientRiskFactors,
  OutreachProjection,
} from '#services/api/mia.ts';

import { getConfirmationWorklist, listRebookingCandidates } from '#services/api/mia.ts';

/**
 * The unconfirmed bucket card promises "Next 2 days" of appointments — keep the fetch window in
 * lockstep with that copy. (Matches the server default; explicit so the copy can't drift when
 * the default changes.)
 */
const unconfirmedWorklistDaysAhead = 2;

export interface RescheduleRow {
  id: number;
  startDate: string;
  eventAt: string;
  eventKind: RebookingEventKind;
  patientBirthDate: string | null;
  patientEmail: string | null;
  patientPhone: string | null;
  preferredChannel: RebookingCandidate['preferredChannel'];
  riskFactors: PatientRiskFactors;
  status: RebookingCandidateStatus;
  outreach: OutreachProjection | null;
  provider: {
    userId: number;
    firstName?: string;
    lastName?: string;
  };
  patient: {
    userId: number;
    firstName?: string;
    lastName?: string;
  };
  appointmentType: {
    id: number;
    name: string;
  };
}

export interface PatientReadinessDetailsResponse {
  unconfirmed: UnconfirmedRow[];
  reschedule: RescheduleRow[];
  /** The server-resolved sender rendered in the bulk Rebooking Outreach example. */
  messageAuthorName: string;
  /**
   * The org's derived risk kind, carried up from the worklist envelope so the briefing and the
   * details grid can phrase themselves in kind-aware copy.
   */
  riskKind: RiskKind;
  /** Carried up from the worklist envelope for the same reason as `riskKind`: it is the server's. */
  confirmationLeadTimeFloorMinutes: number;
}

export interface GetPatientReadinessDetailsParams extends FeedFilters {
  orgId: number;
}

export async function getPatientReadinessDetails(
  params: GetPatientReadinessDetailsParams
): Promise<PatientReadinessDetailsResponse> {
  const [worklistResponse, rebookingCandidatesResponse] = await Promise.all([
    getConfirmationWorklist({ ...params, daysAhead: unconfirmedWorklistDaysAhead }),
    listRebookingCandidates(params),
  ]);

  // Initial selection uses the returned order while the grid applies the same risk-first sort.
  // Keeping this at the fetch boundary avoids recalculating row order in the only consumer.
  return {
    unconfirmed: [...worklistResponse.rows].sort((left, right) => {
      const leftRisk = getUnconfirmedRowRiskSortValue(left);
      const rightRisk = getUnconfirmedRowRiskSortValue(right);

      if (leftRisk !== rightRisk) {
        return rightRisk - leftRisk;
      }

      return (
        new Date(left.appointmentStartsAt).getTime() - new Date(right.appointmentStartsAt).getTime()
      );
    }),
    reschedule: rebookingCandidatesResponse.candidates.map(mapRebookingCandidateToRescheduleRow),
    messageAuthorName: rebookingCandidatesResponse.messageAuthorName,
    riskKind: worklistResponse.riskKind,
    confirmationLeadTimeFloorMinutes: worklistResponse.confirmationLeadTimeFloorMinutes,
  };
}

/**
 * The row's predicted risk for whichever kind the org is scored on: only one of the two
 * probabilities is ever populated, so the coalesce picks it without needing the envelope's
 * `riskKind`. Null means unscored, which every caller must treat as unknown — not as low risk.
 */
export function getUnconfirmedRowRiskProbability(row: UnconfirmedRow): number | null {
  return row.predictedNoShowProbability ?? row.predictedLateCancelProbability;
}

/** Sorts below every real probability, which is bounded to 0..1. */
const unscoredRiskSortValue = -1;

/**
 * The row's risk as a sortable number, with unscored rows pushed last rather than treated as low
 * risk. Shared by this module's fetch-boundary sort and the details grid's risk column so the two
 * orderings can't drift apart.
 */
export function getUnconfirmedRowRiskSortValue(row: UnconfirmedRow): number {
  return getUnconfirmedRowRiskProbability(row) ?? unscoredRiskSortValue;
}

function mapRebookingCandidateToRescheduleRow(candidate: RebookingCandidate): RescheduleRow {
  return {
    id: candidate.appointmentId,
    startDate: candidate.appointmentTime,
    eventAt: candidate.eventAt,
    eventKind: candidate.eventKind,
    patientBirthDate: candidate.patientBirthDate,
    patientEmail: candidate.patientEmail,
    patientPhone: candidate.patientPhone,
    preferredChannel: candidate.preferredChannel,
    riskFactors: candidate.riskFactors,
    status: candidate.status,
    outreach: candidate.outreach,
    provider: {
      userId: candidate.providerId,
      firstName: candidate.providerFirstName ?? undefined,
      lastName: candidate.providerLastName ?? undefined,
    },
    patient: {
      userId: candidate.patientId,
      firstName: candidate.patientFirstName ?? undefined,
      lastName: candidate.patientLastName ?? undefined,
    },
    appointmentType: {
      id: candidate.appointmentTypeId,
      name: candidate.appointmentTypeName,
    },
  };
}
